import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { MarkdownProps } from "./Markdown"
import { MouseEvent } from 'react'
import Debouncer from "reusable/utils/Debouncer"
import { postgresApiClient } from "api/trpc-pg/trpc-client"
import Debug from "reusable/utils/debug"
import * as Sentry from "@sentry/react"
import { makeAutoObservable } from "mobx"
import { snackbarError } from "reusable/mui/snackbar/snackbar"
import { MARKDOWN_LOADING_CLASS, MarkdownLoaderState } from "./MarkdownLoaderStatus"
import { isAdvisorEdit } from "./editorChange"
import type { MDXEditorMethods } from "@mdxeditor/editor"

interface MarkdownStoreProps extends MarkdownProps { }

export default class MarkdownStore {
   sp
   /** Tracks the last markdown string emitted by the editor's onChange, used to prevent setMarkdown feedback loops */
   lastEditorMarkdown: string | undefined = undefined
   private previousMarkdown: string | undefined
   private autoSaveDebouncer = new Debouncer({ debounce: 1500 })
   /** Set just before a programmatic `setMarkdown`, whose `onChange` is indistinguishable from a keystroke. */
   private ignoreNextChange = false

   constructor(sp: MarkdownStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
      this.previousMarkdown = sp.markdown
   }

   get toolbarClass() {
      return this.sp.readOnly ? 'toolbar-hidden' : 'toolbar-visible'
   }

   /** Whether an agent is working, which the bar, the editor lock and the feather fade all read. */
   get loaderState(): MarkdownLoaderState {
      return this.sp.loaderState ?? 'idle'
   }

   /** The placeholder, suppressed while generating since the editor is locked and about to fill. */
   get placeholder(): string | undefined {
      return this.loaderState === 'loading' ? undefined : this.sp.placeholder
   }

   /** Whether the editor rejects input, which generating does as well as the `readOnly` prop. */
   get isReadOnly() {
      return !!this.sp.readOnly || this.loaderState === 'loading'
   }

   get contentClass() {
      return this.loaderState === 'loading' ? `${this.toolbarClass} ${MARKDOWN_LOADING_CLASS}` : this.toolbarClass
   }

   handleError = (payload: { error: string; source: string }) => {
      Debug.log.markdown('MDXEditor error:', payload)
      snackbarError('Unable to load preview. Please contact support if this persists.')
      Sentry.captureException(new Error(payload.error), {
         tags: {
            component: 'MarkdownStore',
            action: 'MDXEditor.onError',
            source: payload.source,
            error: payload.error,
            markdownId: this.sp.id,
            markdownType: JSON.parse(JSON.stringify(this.sp.type)),
         }
      })
   }

   /** Saves a change still waiting on the autosave debounce, now. */
   flushAutoSave = async () => {
      await this.autoSaveDebouncer.flush()
   }

   handleLinkClick = (e: MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement
      const link = target.closest('a')
      if (link && link.href) {
         e.preventDefault()
         window.open(link.href, '_blank', 'noopener,noreferrer')
      }
   }

   /**
    * Writes markdown the user didn't type, without the write counting as an edit.
    * @param params The editor to write to, and the markdown to write.
    */
   applyExternalMarkdown = (params: { editor: MDXEditorMethods, markdown: string }) => {
      const { editor, markdown } = params
      // MDXEditor skips a trim-equal setMarkdown, so there is nothing to write or to guard.
      if (markdown.trim() === editor.getMarkdown().trim()) return

      this.ignoreNextChange = true
      editor.setMarkdown(markdown)
      // MDXEditor mutes the change this write emits, so nothing downstream can close the flag.
      this.ignoreNextChange = false
      this.previousMarkdown = markdown
   }

   /**
    * Wrapper on the onChange. Detect if a GapFile directive have been remove, and ifso, mark the file for deletion
    * @param newMarkdown The editor's markdown after the change.
    * @param isInitialNormalize MDXEditor's flag for the change its own initial import emits.
    */
   handleChange = (newMarkdown: string, isInitialNormalize = false) => {
      this.lastEditorMarkdown = newMarkdown
      if (!isAdvisorEdit({ isProgrammatic: this.ignoreNextChange, isInitialNormalize })) {
         // Not the advisor's, but `previousMarkdown` moves on so the next real edit diffs against it.
         this.ignoreNextChange = false
         this.previousMarkdown = newMarkdown
         return
      }
      const previousFiles = this.extractGapFileNames(this.previousMarkdown)
      const currentFiles = this.extractGapFileNames(newMarkdown)

      // Find files that were removed
      for (const fileName of previousFiles) {
         if (!currentFiles.has(fileName)) {
            this.markFileForDeletion(fileName)
         }
      }

      this.previousMarkdown = newMarkdown
      this.sp.onChange?.(newMarkdown)

      // Trigger debounced auto-save if callback is provided
      if (this.sp.autoSave) {
         this.autoSaveDebouncer.run(() => this.autoSave(newMarkdown))
      }
   }

   private autoSave = async (markdown: string) => {
      // Re-checked here, not where the save is scheduled: the debounce outlasts the start of a generation.
      if (this.loaderState === 'loading') return
      try {
         await postgresApiClient.markdown.saveMarkdown.mutate({ markdown, id: this.sp.id, type: this.sp.type })
      } catch (error: any) {
         Debug.log.markdown('Auto-save failed:', error)
         Sentry.captureException(error, { tags: { component: 'MarkdownStore', action: 'autoSave' }, })
      }
   }

   /** Extract all GapFile and GapImage rawFileName values from markdown */
   private extractGapFileNames(markdown: string | undefined): Set<string> {
      if (!markdown) return new Set()
      const regex = /::?Gap(?:File|Image)\{[^}]*rawFileName="([^"]+)"[^}]*\}/g
      const fileNames = new Set<string>()
      let match
      while ((match = regex.exec(markdown)) !== null) {
         fileNames.add(match[1])
      }
      return fileNames
   }

   // When a file gets removed in markdown, we need to update the entity and mark the file for deletion
   // Cronjob will later take care of the deletion of the file from s3(unless the user pressed undo)
   private markFileForDeletion = async (rawFileName: string) => {
      try {
         await postgresApiClient.markdown.markFileForDeletion.mutate({ fileName: rawFileName, id: this.sp.id, type: this.sp.type })
      } catch (error) {
         Debug.log.markdown('Unable to mark file for deletion:', rawFileName, error)
      }
   }
}

export const { Provider: MarkdownStoreProvider, useProvidedStore: useMarkdownStore } = createStoreContext(MarkdownStore)
